export const FREQUENCY_IN_MINUTES = {
  '30': '30',
  '60': '60',
  '120': '120',
  '180': '180'
};


export const FREQUENCY_TYPE = {
  1: 'One-Time',
  2: 'Recurring',
};

export const FREQUENCY_BY = {
  1: 'Daily',
  2: 'Weekly',
};

export const WEEK_DAYS = {
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
};

export const FREQUENCY_BY_ID = {
  Daily: 1,
  Weekly: 2,
};

export const FREQUENCY_TYPE_ID = {
  OneTime: 1,
  Recurring: 2,
};

export enum DATA_CONTEXT_GROUP {
  None = 0,
  Patient = 1
}

export const LOAD_TYPE =
{
  1: "Incremental",
  2: "Full Load",
  3: "On Demand",
  4: "Initial Full Load",
}

export const LOAD_TYPE_ID = {
  Incremental: 1,
  FullLoad: 2,
  OnDemand: 3,
  InitialFullLoad: 4
} as const;

export const DATA_CONTEXTS = {
  Patient: 101,
  Services: 102,
  Category: 103,
  Fees: 104,
  TransactionsHeader: 105,
  TransactionsDetail: 106,
  InsuranceCompany: 107,
  Practice: 108,
  Provider: 109,
  AppointmentTypes: 110,
  Appointment: 111,
  PlannedServices: 112,
  Transactions: 113,
  AppointmentDetail: 114,
  Employer: 115,
  AppointmentProvider: 116,
  Paytype: 117,
  Users: 118,
  InsuranceClaim: 119,
  TaxonomyCode: 120,
  AdjustmentType: 121,
}


export enum WIDGET_STATUS {
    NotDownloaded = 1,
		Downloaded = 2,
		Installed = 3,
		Uninstalled = 4
}

export enum PMS_CONFIG_STEPS {
  PMS_DETAILS = 'PMS Details',
  WIDGET_STATUS = 'Widget Status',
  PRACTICE_MAPPING = 'Practice Mapping',
  DATA_SYNC_CONFIGURATION = 'Data Sync Configuration',
} 

export enum PMS_CONFIG_STEPS_SEQ {
  PMS_DETAILS = 1,
  WIDGET_STATUS = 2,
  PRACTICE_MAPPING = 3,
  DATA_SYNC_CONFIGURATION = 4,
} 

export enum INFO_STEP_KEY {
  Step1 = 'Step1',
  Step2 = 'Step2',
  Step3 = 'Step3',
  Step4 = 'Step4',
}

export enum PmsInstructionVariant {
  OpenDental = 'OpenDental',
  Eaglesoft = 'Eaglesoft',
}

export interface InfoStepVariant {
  label: string;
  steps: any[];
}

export interface InfoStepConfig {
  title: string;
  description?: string;
  steps?: string[];                                   // used when no PMS variant applies (Step 2-4)
  variants?: Record<PmsInstructionVariant, InfoStepVariant>; // used only for Step 1
}

export const INFO_STEPS: Record<INFO_STEP_KEY, InfoStepConfig> = {
  [INFO_STEP_KEY.Step1]: {
    title: 'Step 1: Link Your PMS Details',
    variants: {
      [PmsInstructionVariant.OpenDental]: {
        label: 'Open Dental Instructions',
        steps: [
          'You need to add your Tooth Teller Customer Key into your Open Dental software so the systems can communicate.',
          'Copy the Customer Key displayed in the Step 1 card.',
          'Open your Open Dental software on your computer.',
          ['Navigate to Setup > Advanced Setup > API.','In the pop-up window, click the Add Key button.','Paste the Customer Key and click <b>Save.</b>'],
          'Return to this portal and check the box confirming you have successfully added the API key in Open Dental.',
          'Click <b>Next</b> to proceed to the widget installation.',
        ],
      },
      [PmsInstructionVariant.Eaglesoft]: {
        label: 'Eaglesoft Instructions',
        steps: [
          "To connect Eaglesoft, you need to provide the exact file path to your system's <b>EaglesoftSettings.dll</b> file so the widget can locate your database.",
          'Open File Explorer on the computer or server where Eaglesoft is installed.',
          'Navigate to your Eaglesoft installation folder. (Typically located at C:\\Eaglesoft or C:\\Program Files (x86)\\Patterson Companies\\Eaglesoft).',
          'Scroll through the folder to locate the file named <b><i>EaglesoftSettings.dll.</i></b>',
          'Copy the file path — Quick Method (Windows): Hold down the Shift key, right-click on the EaglesoftSettings.dll file, and select "Copy as path".',
          'Alternative Method: Click the folder address bar at the top, copy the folder path, and manually add \\EaglesoftSettings.dll to the end.',
          'Return to this portal and paste the complete file path into the "Add Path" field on the left.',
          'Click <b>Next</b> to proceed to the widget installation.',
        ],
      },
    },
  },

  [INFO_STEP_KEY.Step2]: {
    title: 'Step 2: Install the Tooth Teller Widget',
    description: 'The widget acts as a bridge between your local database and Tooth Teller.',
    steps: [
      'Download the Tooth Teller Widget using the <b>download button.</b>',
      'Double-click the downloaded file to launch the installer.',
      'Once the wizard opens, click <b>Next</b> to proceed.',
      'Check the box to accept the License Agreement and click <b>Next.</b>',
      'Select your preferred installation path (or leave the default) and click <b>Next.</b>',
      'Copy the Widget License Key using the <b>copy button.</b>',
      'Paste the license key into the installer when prompted, then click <b>Validate.</b>',
      'Wait for the installation to finish, then click <b>Test PMS Connection.</b>',
      'Once successful, click <b>Finish.</b>',
      'Come back to this portal and check the widget status should be updated to "Installed".',
      'Click <b>Next</b> to proceed to the Practice Mapping step.',
    ],
  },

  [INFO_STEP_KEY.Step3]: {
    title: 'Step 3: Map the Right Practice',
    description: 'Now that the widget is connected, we need to know which practice data to sync.',
    steps: [
      'The dropdown menu in the Step 3 card will automatically populate with all practices found in your PMS.',
      'Click the Select Practice to Onboard dropdown.',
      'Choose the specific practice you wish to connect to this account.',
      'Click <b>Next</b> to proceed to the Data Sync Configuration step.',
    ],
  },

  [INFO_STEP_KEY.Step4]: {
    title: 'Step 4: Configure Data Refresh Settings',
    description: 'Tell us how often to pull and update your data.',
    steps: [
      'Initial Data Load: Set your initial load date, time, and timezone. Select how far back you want to extract historical data (ranging from 1 to 5 years, or All Time).',
      'Daily Full Sync: Set the daily time for Tooth Teller to perform a full synchronization, and select the historical time frame for this daily sync.',
      'New Data Update Frequency: Choose how often the system checks for new updates. Note: We highly recommend setting this to Every 15 minutes.',
      'Click <b>Finish</b> to complete your PMS configuration.',
    ],
  },
};